import { apiSurveys } from "@/api/survey.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { ReminderSettingsPayload } from "@/types/reminder.types.ts";
import {
  ApiSurveyFilter,
  ApiSurveyParticipantFilter,
  ApiSurveyResultsFilter,
  ApiSurveyAggregatedResultsFilter,
  ApiSurveyStatus,
  ApiSurveyUpdateDetails,
  ApiSurveyUpdateForm,
  ApiSurveyUpdateLaunch,
  ApiSurveyReopen,
} from "@/types/survey.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { SelectItem } from "@skailer-com/uikit";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";

export const surveysKeys = createQueryKeys("surveys", {
  search: (filter: ApiSurveyFilter) => ({ queryKey: [filter] }),
  details: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Survey id is required");
      const result = await apiSurveys.getById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  aggregatedResults: (id: number | undefined, filter: ApiSurveyAggregatedResultsFilter) => ({
    queryKey: [id, filter],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Survey id is required");
      const result = await apiSurveys.getSurveyAggregatedResults(id, filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),

  setup: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Survey id is required");
      const result = await apiSurveys.getByIdEdit(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  participantSearch: (filter: ApiSurveyParticipantFilter) => ({ queryKey: [filter] }),

  participantResults: (surveyId: number, employeeId: number) => ({
    queryKey: [surveyId, employeeId],
    queryFn: async () => {
      const result = await apiSurveys.participantResultByEmployee(surveyId, employeeId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data!;
    },
  }),

  questionResultsSearchInfinite: (filter: ApiSurveyResultsFilter) => ({ queryKey: [filter] }),

  chart: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Survey id is required");
      const result = await apiSurveys.getChart(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useSurveySearch = ({
  filter,
  enabled = true,
}: {
  filter: ApiSurveyFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    ...surveysKeys.search(filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiSurveys.search(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useSurveysIsRunningSearchInline = (
  enabled: boolean = true,
  defaultFilter?: ApiSurveyFilter
) => {
  const [filter, setFilter] = useState<ApiSurveyFilter>({
    page_number: 1,
    ...(defaultFilter ?? {}),
    statuses: [ApiSurveyStatus.running],
  });

  const requestFilter = useMemo<ApiSurveyFilter>(
    () => ({
      ...filter,
      statuses: [ApiSurveyStatus.running],
    }),
    [filter]
  );

  const { data, isLoading, isFetching } = useSurveySearch({ filter: requestFilter, enabled });

  const options: SelectItem[] = useMemo(() => {
    return (
      data?.items.map((item) => ({
        id: item.id,
        title: item.title,
      })) ?? []
    );
  }, [data]);

  function search(text: string | undefined) {
    setFilter((currentFilter) => ({
      ...currentFilter,
      page_number: 1,
      text: text?.trim() || undefined,
      statuses: [ApiSurveyStatus.running],
    }));
  }

  return {
    data,
    isLoading,
    isFetching,
    options,
    search,
    filter,
    setFilter,
  };
};

export const useSurveyDetails = (id: number | undefined, enabled = true) => {
  return useQuery({
    ...surveysKeys.details(id),
    enabled: !!id && enabled,
  });
};

export const useSurveyAggregatedResults = (
  id: number | undefined,
  filter: ApiSurveyAggregatedResultsFilter
) => {
  return useQuery({
    ...surveysKeys.aggregatedResults(id, filter),
    enabled: !!id,
  });
};

export const useSurveySetup = (id: number | undefined, enabled = true) => {
  return useQuery({
    ...surveysKeys.setup(id),
    enabled: !!id && enabled,
  });
};

export const useSurveyParticipantSearch = ({
  filter,
  enabled = true,
}: {
  filter: ApiSurveyParticipantFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    ...surveysKeys.participantSearch(filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiSurveys.participantsSearch(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useSurveyCreate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  // const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function () {
      const result = await apiSurveys.create();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      // queryClient.invalidateQueries({
      //   queryKey: employeesKeys.search._def,
      // });
      // queryClient.invalidateQueries({
      //   queryKey: employeesKeys.detail(employee.id).queryKey,
      // });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyDuplicate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiSurveys.duplicate(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyUpdateDetails = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: { id: number; payload: ApiSurveyUpdateDetails }) {
      const result = await apiSurveys.updateSurveyDetails(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: surveysKeys.setup._def,
      });
      queryClient.invalidateQueries({
        queryKey: surveysKeys.search._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyUpdateForm = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: { id: number; payload: ApiSurveyUpdateForm }) {
      const result = await apiSurveys.updateSurveyForm(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: surveysKeys.setup._def,
      });
      queryClient.invalidateQueries({
        queryKey: surveysKeys.search._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyResetForm = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiSurveys.updateSurveyForm(id, { reset_form: true });
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: surveysKeys.setup._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyUpdateLaunch = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: { id: number; payload: ApiSurveyUpdateLaunch }) {
      const result = await apiSurveys.updateSurveyLaunch(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: surveysKeys.setup._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyUpdateReminders = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: { id: number; payload: ReminderSettingsPayload }) {
      const result = await apiSurveys.updateReminders(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: surveysKeys.setup._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyLaunch = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiSurveys.launch(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: surveysKeys.details(payload.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: surveysKeys.aggregatedResults._def,
        predicate: (query) => query.queryKey[2] === payload.id,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyPause = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiSurveys.pause(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: surveysKeys.details(payload.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: surveysKeys.aggregatedResults._def,
        predicate: (query) => query.queryKey[2] === payload.id,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyComplete = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiSurveys.complete(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: surveysKeys.details(payload.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: surveysKeys.aggregatedResults._def,
        predicate: (query) => query.queryKey[2] === payload.id,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyReopen = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: { id: number; payload: ApiSurveyReopen }) {
      const result = await apiSurveys.reopen(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: surveysKeys.details(variables.id).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyArchive = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiSurveys.archive(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: surveysKeys.setup._def,
      });
      queryClient.invalidateQueries({
        queryKey: surveysKeys.search._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyParticipantResults = (
  surveyId: number,
  employeeId: number,
  enabled?: boolean
) => {
  return useQuery({
    ...surveysKeys.participantResults(surveyId, employeeId),
    enabled: enabled,
  });
};

export const useSurveyQuestionResultsSearchInfinite = (filter: ApiSurveyResultsFilter) => {
  return useInfiniteQuery({
    queryKey: surveysKeys.questionResultsSearchInfinite(filter).queryKey,
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await apiSurveys.questionResultsSearch(
        { ...filter, page_number: pageParam },
        signal
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPageParam) =>
      lastPageParam && lastPageParam.metadata.page_number < lastPageParam.metadata.total_pages
        ? lastPageParam.metadata.page_number + 1
        : undefined,
  });
};

export const useSurveyChart = (id: number | undefined, enabled = true) => {
  return useQuery({
    ...surveysKeys.chart(id),
    enabled: !!id && enabled,
  });
};
